import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Caption1, MessageBar, MessageBarBody, Spinner, Text, makeStyles } from '@fluentui/react-components';
import { formatRecordedCalendarDate, agencyCalendarDate, type PersonFileListItem } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import { fetchAuthState } from '@/lib/auth';
import { fetchPersonFiles } from './api.js';
import { downloadFile, problemCopy, problemOf } from './download.js';
import { FileViewer, VIEW_RECORDED_COPY } from './FileViewer.js';
import { formatByteLength, groupFiles, typeLabel, viewerKindFor } from './model.js';

const useStyles = makeStyles({
  panel: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0 },
  group: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, minWidth: 0 },
  h: { margin: 0, fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold },
  list: { listStyleType: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' },
  row: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalS, minWidth: 0, overflowWrap: 'anywhere',
    paddingBlock: tokens.spacingVerticalS, borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  grow: { flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS },
});

/** The query key the Files section and every stored-file link share. */
export const personFilesKey = (personId: string) => ['person-files', personId] as const;

/**
 * Whether this user may read files at all. `files.read` is Admin-only (auth/policy.ts).
 *
 * It exists so nothing ASKS for a list the API refuses by design. #270 gated the Files section on
 * the person page for exactly that reason, but `StoredFileLink` — which reads the same list to
 * resolve a step's evidence — is also rendered from History and from the Hiring section, and both
 * still fired the refused request. The browser specs treat any console error as a failure, so that
 * is a red run waiting for a viewer journey that happens to pass a step with a committed file.
 *
 * Reads the same cached `['auth']` query the pages use — same key, same options — so it costs no
 * extra request. `staleTime` is stated HERE rather than relied on from the global default in
 * `main.tsx`: this hook subscribes as a new observer after the page's own `canEdit` has resolved, so
 * with a smaller global default react-query would refetch `['auth']` on that mount. The property now
 * belongs to the hook instead of to a setting nothing pins.
 */
export function useMayReadFiles(): boolean {
  const auth = useQuery({ queryKey: ['auth'], queryFn: fetchAuthState, retry: false, staleTime: 30_000 });
  return auth.data?.status === 'signed-in' && auth.data.user.isAdmin;
}

/**
 * The person's committed files.
 *
 * `enabled` DEFAULTS TO FALSE, so an omitted argument makes no request. It defaulted to `true` while
 * a comment here claimed it "defaults to the caller's own capability check so no consumer can forget
 * it" — untrue, and fail-OPEN for a capability gate on public surface (`usePersonFiles` is exported
 * from this feature's index). That is the exact failure this PR exists to fix: gating each caller is
 * what left two of the three ungated. Both current callers pass `useMayReadFiles()` explicitly, so
 * nothing changes today; a future one that forgets now gets no request rather than an unguarded one.
 */
export function usePersonFiles(personId: string, enabled = false) {
  return useQuery({ queryKey: personFilesKey(personId), queryFn: () => fetchPersonFiles(personId), retry: false, enabled });
}

export interface FilesPanelProps {
  readonly personId: string;
}

/**
 * A person's committed files (ADR-009 §4), read from `GET /api/people/{id}/files`. I-9 and
 * health files are grouped apart and badged. The API already leaves them out for anyone
 * without `files.restricted.read`, so this renders what it is given and assumes nothing.
 */
export function FilesPanel({ personId }: FilesPanelProps) {
  const styles = useStyles();
  const mayRead = useMayReadFiles();
  const files = usePersonFiles(personId, mayRead);
  const [open, setOpen] = useState<PersonFileListItem | null>(null);

  // Its own gate, rather than trusting every caller to have one. `PersonPage` does gate this
  // section, but a panel that sits on a spinner forever when it is not allowed to read is a trap
  // for the next caller.
  // While `/api/app/me` is in flight `mayRead` is false, so an admin would briefly read this. It
  // corrects itself when auth resolves, and it is unreachable through the app today because
  // `AppLayout` renders a "Checking your access" spinner instead of the outlet while auth is
  // pending, and this panel only ever renders inside it. Recorded because it is a property of the
  // component, not of the shell: rendered on a route outside `AppLayout`, branch on
  // `auth.isPending` separately rather than folding it into `!mayRead`.
  if (!mayRead) {
    return <MessageBar intent="warning"><MessageBarBody>Files are available to HR admins.</MessageBarBody></MessageBar>;
  }
  if (files.isPending) return <Spinner size="small" label="Loading files" />;
  if (files.isError) {
    const problem = problemOf(files.error);
    return (
      <MessageBar intent={problem === 'unavailable' ? 'warning' : 'error'}>
        <MessageBarBody>
          {problem === 'not-found' ? 'Files are not available for this person, or you do not have access to them.' : problemCopy[problem]}
        </MessageBarBody>
      </MessageBar>
    );
  }
  const groups = groupFiles(files.data.files);
  if (groups.personnel.length === 0 && groups.restricted.length === 0) {
    return <Text>No files have been stored for this person yet. Approved uploads appear here.</Text>;
  }

  return (
    <div className={styles.panel}>
      <Caption1>{VIEW_RECORDED_COPY}</Caption1>
      {groups.personnel.length > 0 ? (
        <FileGroup heading="Personnel file" items={groups.personnel} personId={personId} onOpen={setOpen} />
      ) : null}
      {groups.restricted.length > 0 ? (
        <FileGroup heading="Restricted: I-9 and health" items={groups.restricted} personId={personId} onOpen={setOpen} />
      ) : null}
      <FileViewer personId={personId} item={open} onClose={() => setOpen(null)} />
    </div>
  );
}

function FileGroup({ heading, items, personId, onOpen }: {
  readonly heading: string;
  readonly items: readonly PersonFileListItem[];
  readonly personId: string;
  readonly onOpen: (item: PersonFileListItem) => void;
}) {
  const styles = useStyles();
  return (
    <section className={styles.group} aria-label={heading}>
      <h4 className={styles.h}>{heading}</h4>
      <ul className={styles.list}>
        {items.map((item) => <FileRow key={item.fileId} item={item} personId={personId} onOpen={onOpen} />)}
      </ul>
    </section>
  );
}

function FileRow({ item, personId, onOpen }: { readonly item: PersonFileListItem; readonly personId: string; readonly onOpen: (item: PersonFileListItem) => void }) {
  const styles = useStyles();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const viewable = viewerKindFor(item.contentType) !== 'download';

  async function download() {
    setBusy(true);
    setProblem(null);
    try {
      await downloadFile(personId, item);
    } catch (error) {
      setProblem(problemCopy[problemOf(error)]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className={styles.row}>
      <div className={styles.grow}>
        <Text weight="semibold">{item.displayName}</Text>
        <Caption1>
          {item.categoryLabel} · {typeLabel(item.contentType)} · {formatByteLength(item.byteLength)} · Stored {formatRecordedCalendarDate(agencyCalendarDate(item.committedAt))}
        </Caption1>
        {problem === null ? null : <Caption1 role="alert">{problem}</Caption1>}
      </div>
      {item.segregated ? <Badge appearance="outline" size="medium" shape="rounded">Restricted</Badge> : null}
      <div className={styles.actions}>
        {viewable ? <Button size="small" onClick={() => onOpen(item)} aria-label={`Open ${item.displayName}`}>Open</Button> : null}
        <Button size="small" appearance="subtle" disabled={busy} onClick={() => void download()} aria-label={`Download ${item.displayName}`}>
          {busy ? 'Downloading…' : 'Download'}
        </Button>
      </div>
    </li>
  );
}
